import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { tokens } from '../../design-system';
import { useHelp } from './HelpProvider';
import { helpSections, pageGuides, keyboardShortcuts } from './helpContent';
import { XMarkIcon, MagnifyingGlassIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const SectionHeader = ({ title, isOpen, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      padding: `${tokens.spacing[3]} 0`,
      background: 'none',
      border: 'none',
      borderBottom: `1px solid ${tokens.colors.neutral[700]}`,
      color: tokens.colors.neutral[100],
      fontSize: tokens.typography.fontSize.sm[0],
      fontWeight: tokens.typography.fontWeight.semibold,
      cursor: 'pointer',
      textAlign: 'left',
      letterSpacing: '0.02em',
    }}
  >
    {title}
    <ChevronDownIcon
      style={{
        width: 16,
        height: 16,
        color: tokens.colors.neutral[400],
        transition: 'transform 200ms ease',
        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      }}
    />
  </button>
);

const ArticleItem = ({ article, isExpanded, onToggle, highlight }) => {
  const titleText = article.title;
  return (
    <div style={{ borderBottom: `1px solid ${tokens.colors.neutral[700]}` }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: `${tokens.spacing[2]} ${tokens.spacing[2]}`,
          background: 'none',
          border: 'none',
          color: isExpanded ? tokens.colors.primary[300] : tokens.colors.neutral[200],
          fontSize: tokens.typography.fontSize.sm[0],
          fontWeight: tokens.typography.fontWeight.medium,
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'color 150ms ease',
        }}
      >
        {titleText}
        <ChevronDownIcon
          style={{
            width: 14,
            height: 14,
            color: tokens.colors.neutral[400],
            transition: 'transform 200ms ease',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            flexShrink: 0,
            marginLeft: tokens.spacing[2],
          }}
        />
      </button>
      {isExpanded && (
        <div
          style={{
            padding: `0 ${tokens.spacing[3]} ${tokens.spacing[3]}`,
            fontSize: tokens.typography.fontSize.sm[0],
            lineHeight: '1.7',
            color: tokens.colors.neutral[300],
            whiteSpace: 'pre-line',
          }}
        >
          {article.body}
        </div>
      )}
    </div>
  );
};

const HelpCenterPanel = () => {
  const {
    helpPanelOpen,
    closeHelpPanel,
    helpPanelSearch,
    setHelpPanelSearch,
  } = useHelp();

  const location = useLocation();

  const [openSections, setOpenSections] = useState({
    'getting-started': true,
    'key-concepts': false,
    'page-guide': true,
    'shortcuts': false,
  });
  const [expandedArticle, setExpandedArticle] = useState(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  // Focus search when panel opens
  useEffect(() => {
    if (helpPanelOpen && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 350);
    }
  }, [helpPanelOpen]);

  // Close on Escape
  useEffect(() => {
    if (!helpPanelOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') closeHelpPanel();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [helpPanelOpen, closeHelpPanel]);

  const toggleSection = (id) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleArticle = (id) => {
    setExpandedArticle((prev) => (prev === id ? null : id));
  };

  const query = (helpPanelSearch || '').toLowerCase().trim();

  const filterArticles = (articles) => {
    if (!query) return articles;
    return articles.filter(
      (a) =>
        a.title.toLowerCase().includes(query) ||
        a.body.toLowerCase().includes(query)
    );
  };

  const filteredGettingStarted = filterArticles(helpSections.gettingStarted.articles);
  const filteredKeyConcepts = filterArticles(helpSections.keyConcepts.articles);
  // Map current route to a page guide key
  const pageGuideKey = location.pathname === '/dashboard' ? 'dashboard' : null;
  const currentGuide = pageGuideKey ? pageGuides[pageGuideKey] : null;
  const filteredTips = currentGuide
    ? query
      ? currentGuide.tips.filter((t) => t.toLowerCase().includes(query))
      : currentGuide.tips
    : [];
  const filteredShortcuts = query
    ? keyboardShortcuts.filter(
        (s) => s.keys.toLowerCase().includes(query) || s.action.toLowerCase().includes(query)
      )
    : keyboardShortcuts;

  return (
    <>
      {/* Backdrop */}
      {helpPanelOpen && (
        <div
          onClick={closeHelpPanel}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            zIndex: 1299,
            transition: 'opacity 300ms ease',
          }}
        />
      )}

      {/* Panel */}
      <div
        ref={panelRef}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 400,
          maxWidth: '100vw',
          backgroundColor: tokens.colors.neutral[950],
          borderLeft: `1px solid ${tokens.colors.neutral[700]}`,
          zIndex: 1300,
          transform: helpPanelOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 300ms ease',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: `${tokens.spacing[4]} ${tokens.spacing[5]}`,
            borderBottom: `1px solid ${tokens.colors.neutral[700]}`,
            flexShrink: 0,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: tokens.typography.fontSize.lg[0],
              fontWeight: tokens.typography.fontWeight.bold,
              color: tokens.colors.neutral[0],
            }}
          >
            Help Center
          </h2>
          <button
            type="button"
            onClick={closeHelpPanel}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              background: 'none',
              border: 'none',
              color: tokens.colors.neutral[300],
              cursor: 'pointer',
              borderRadius: tokens.borderRadius.sm,
              padding: 0,
            }}
          >
            <XMarkIcon style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* Search */}
        <div
          style={{
            padding: `${tokens.spacing[3]} ${tokens.spacing[5]}`,
            borderBottom: `1px solid ${tokens.colors.neutral[700]}`,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: tokens.spacing[2],
              backgroundColor: tokens.colors.neutral[800],
              border: `1px solid ${tokens.colors.neutral[700]}`,
              borderRadius: tokens.borderRadius.md,
              padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
            }}
          >
            <MagnifyingGlassIcon
              style={{ width: 16, height: 16, color: tokens.colors.neutral[400], flexShrink: 0 }}
            />
            <input
              ref={searchRef}
              type="text"
              value={helpPanelSearch}
              onChange={(e) => setHelpPanelSearch(e.target.value)}
              placeholder="Search help..."
              style={{
                flex: 1,
                background: 'none',
                border: 'none',
                outline: 'none',
                color: tokens.colors.neutral[100],
                fontSize: tokens.typography.fontSize.sm[0],
              }}
            />
          </div>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: `0 ${tokens.spacing[5]}`,
          }}
        >
          {/* Page Guide (context-aware, shown first) */}
          {currentGuide && filteredTips.length > 0 && (
            <div style={{ marginTop: tokens.spacing[3] }}>
              <SectionHeader
                title={`This Page: ${currentGuide.title}`}
                isOpen={openSections['page-guide']}
                onToggle={() => toggleSection('page-guide')}
              />
              {openSections['page-guide'] && (
                <div style={{ padding: `${tokens.spacing[2]} 0` }}>
                  <p
                    style={{
                      margin: `0 0 ${tokens.spacing[2]}`,
                      fontSize: tokens.typography.fontSize.sm[0],
                      color: tokens.colors.neutral[300],
                      lineHeight: '1.5',
                    }}
                  >
                    {currentGuide.description}
                  </p>
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: tokens.spacing[4],
                      listStyleType: 'disc',
                    }}
                  >
                    {filteredTips.map((tip, i) => (
                      <li
                        key={i}
                        style={{
                          fontSize: tokens.typography.fontSize.sm[0],
                          lineHeight: '1.7',
                          color: tokens.colors.neutral[200],
                          marginBottom: tokens.spacing[1],
                        }}
                      >
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Getting Started */}
          {filteredGettingStarted.length > 0 && (
            <div style={{ marginTop: tokens.spacing[3] }}>
              <SectionHeader
                title={helpSections.gettingStarted.title}
                isOpen={openSections['getting-started']}
                onToggle={() => toggleSection('getting-started')}
              />
              {openSections['getting-started'] && (
                <div>
                  {filteredGettingStarted.map((article) => (
                    <ArticleItem
                      key={article.id}
                      article={article}
                      isExpanded={expandedArticle === article.id}
                      onToggle={() => toggleArticle(article.id)}
                      highlight={query}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Key Concepts */}
          {filteredKeyConcepts.length > 0 && (
            <div style={{ marginTop: tokens.spacing[3] }}>
              <SectionHeader
                title={helpSections.keyConcepts.title}
                isOpen={openSections['key-concepts']}
                onToggle={() => toggleSection('key-concepts')}
              />
              {openSections['key-concepts'] && (
                <div>
                  {filteredKeyConcepts.map((article) => (
                    <ArticleItem
                      key={article.id}
                      article={article}
                      isExpanded={expandedArticle === article.id}
                      onToggle={() => toggleArticle(article.id)}
                      highlight={query}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Keyboard Shortcuts */}
          {filteredShortcuts.length > 0 && (
            <div style={{ marginTop: tokens.spacing[3] }}>
              <SectionHeader
                title="Keyboard Shortcuts"
                isOpen={openSections['shortcuts']}
                onToggle={() => toggleSection('shortcuts')}
              />
              {openSections['shortcuts'] && (
                <div style={{ padding: `${tokens.spacing[2]} 0` }}>
                  {filteredShortcuts.map((s, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: `${tokens.spacing[2]} ${tokens.spacing[2]}`,
                      }}
                    >
                      <span
                        style={{
                          fontSize: tokens.typography.fontSize.sm[0],
                          color: tokens.colors.neutral[200],
                        }}
                      >
                        {s.action}
                      </span>
                      <kbd
                        style={{
                          display: 'inline-block',
                          padding: `2px ${tokens.spacing[2]}`,
                          backgroundColor: tokens.colors.neutral[800],
                          borderRadius: tokens.borderRadius.sm,
                          fontSize: tokens.typography.fontSize.sm[0],
                          color: tokens.colors.neutral[100],
                          border: `1px solid ${tokens.colors.neutral[600]}`,
                          fontFamily: tokens.typography.fontFamily.mono,
                        }}
                      >
                        {s.keys}
                      </kbd>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </>
  );
};

export default HelpCenterPanel;
